'use client';
import clsx from 'clsx';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';

import idsTokens from '../../utils/tokens/idsTokens';
import withRef from '../../utils/withRef';
import MenuItem from '../MenuItem';
import PopperMenu from '../PopperMenu';

const ITEM_HEIGHT = 40;
const SMALL_ITEM_HEIGHT = 32;
const MAX_ITEMS = 10;

export class DropMenu extends React.Component {
  state = {
    anchorEl: null,
    open: false,
  };

  handleClick = (e) => {
    const { onClick, onOpenChange } = this.props;
    if (onClick) {
      onClick(e);
    }
    onOpenChange?.(true);
    this.setState({ open: true, anchorEl: e.currentTarget });
  };

  handleClose = () => {
    this.setState({ open: false });
    this.props.onOpenChange?.(false);
  };

  render() {
    const {
      menuItems = [],
      id,
      MenuAnchor,
      minMenuWidth = 80,
      size,
      classes,
      PopperMenuProps = {},
      disablePortal = false,
      forwardedRef: ref,
      maxItems = MAX_ITEMS,
      placement = 'bottom-start',
      onClick,
      onOpenChange: _omitOnOpenChange,
      ...rest
    } = this.props;
    const { open, anchorEl } = this.state;

    return (
      <>
        <MenuAnchor
          onClick={this.handleClick}
          aria-owns={open ? id : null}
          aria-haspopup="true"
          aria-expanded={open}
          open={open}
          size={size}
          ref={ref}
          {...rest}
        />
        <PopperMenu
          id={id}
          open={open}
          anchorEl={anchorEl}
          placement={placement}
          disablePortal={disablePortal}
          onClose={this.handleClose}
          sx={{
            paddingTop: idsTokens.menu.default.spacing.padding.top,
            paddingRight: idsTokens.menu.default.spacing.padding.right,
            paddingBottom: idsTokens.menu.default.spacing.padding.bottom,
            paddingLeft: idsTokens.menu.default.spacing.padding.left,
            gap: idsTokens.menu.default.spacing.padding.gap,
            background: idsTokens.menu.default.color.background.list,
          }}
          {...PopperMenuProps}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 4],
              },
            },
            ...(PopperMenuProps.modifiers ?? []),
          ]}
          PaperProps={{
            ...PopperMenuProps?.PaperProps,
            sx: {
              maxHeight: (size === 'small' ? SMALL_ITEM_HEIGHT : ITEM_HEIGHT) * maxItems,
              minWidth: minMenuWidth,
              overflowX: 'hidden',
              overflowY: 'auto',
              ...PopperMenuProps?.PaperProps?.sx,
            },
          }}
        >
          {menuItems.map(
            ({ onClick, text, isTitle, label, className, disabled, sx = [], key, ...rest }) => (
              <MenuItem
                onClick={
                  isTitle
                    ? noop
                    : () => {
                        onClick && onClick();
                        this.handleClose();
                      }
                }
                key={key ?? text ?? label}
                disableRipple
                disabled={isTitle || disabled}
                className={clsx(
                  (size === 'small' || size === 'extraSmall') && 'Mui-sizeSmall',
                  className
                )}
                {...rest}
                sx={[
                  isTitle
                    ? {
                        background: `${idsTokens.listItem.default.color.background.enabled}`,
                        cursor: 'default',
                        fontWeight: '700 !important',
                        color: `${idsTokens.listItem.default.color.text.label.enabled} !important`,
                        opacity: 'unset !important',

                        '&:hover': {
                          background: `${idsTokens.listItem.default.color.background.enabled} !important`,
                          color: `${idsTokens.listItem.default.color.text.label.enabled} !important`,
                        },

                        '&:focus': {
                          border: '2px solid rgba(0,0,0,0) !important',
                        },
                        '&:active': {
                          background: 'unset !important',
                          color: `${idsTokens.listItem.default.color.text.label.enabled} !important`,
                        },
                      }
                    : {
                        gap: idsTokens.menu.default.spacing.padding.gap,
                        paddingTop: idsTokens.menu.default.spacing.padding.top,
                        paddingRight: idsTokens.menu.default.spacing.padding.right,
                        paddingBottom: idsTokens.menu.default.spacing.padding.bottom,
                        paddingLeft: idsTokens.menu.default.spacing.padding.left,
                      },
                  ...(Array.isArray(sx) ? sx : [sx]),
                ]}
              >
                {text ?? label}
              </MenuItem>
            )
          )}
        </PopperMenu>
      </>
    );
  }
}

DropMenu.propTypes = {
  /** The button content. */
  buttonText: PropTypes.node,
  /** If `true`, the component is disabled. */
  disabled: PropTypes.bool,
  /** If `true`, the component takes up the full width of its container. */
  fullWidth: PropTypes.bool,
  /** ID of the menu. */
  id: PropTypes.string,
  /** Maximum number of menu items shown in the menu. */
  maxItems: PropTypes.number,
  /** The component used to position the dropdown. */
  MenuAnchor: PropTypes.elementType,
  /** The menu items to display in the dropdown. */
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      /** If `true`, destructive action style will be applied. */
      destructiveAction: PropTypes.bool,
      /** If `true`, the component is disabled. */
      disabled: PropTypes.bool,
      /** The label content. Interchangeable with `text`. */
      label: PropTypes.node,
      /** Callback fired when the component is clicked. */
      onClick: PropTypes.func,
      /** The label content. Interchangeable with `label`. */
      text: PropTypes.node,
    })
  ),
  /** The minimum width of the menu. */
  minMenuWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Callback fired when the menu anchor is clicked. Called before opening the menu. */
  onClick: PropTypes.func,
  /** Callback fired when the menu open state changes (`true` when opened, `false` when closed). Used by `Tooltip` to hide while the menu is open. */
  onOpenChange: PropTypes.func,
  /** Placement of the `Popper`. */
  placement: PropTypes.oneOf([
    'auto-end',
    'auto-start',
    'auto',
    'bottom-end',
    'bottom-start',
    'bottom',
    'left-end',
    'left-start',
    'left',
    'right-end',
    'right-start',
    'right',
    'top-end',
    'top-start',
    'top',
  ]),
  /** Props applied to the `PopperMenu` component. */
  PopperMenuProps: PropTypes.object,
  /** The size of the component. */
  size: PropTypes.oneOf(['extraSmall', 'small', 'medium']),
};

export default withRef()(DropMenu);
